import dayjs from 'dayjs';
import { RefObject, useRef, useCallback, useMemo } from 'react';
import { computeDateRange, isSameDateRange } from '../../../../helpers/date';
import DatesRangeSelector from '../../../controls/DatesRangeSelector';
import isBetween from 'dayjs/plugin/isBetween';

interface StringDateRange {
  start: any;
  end: any;
  flexibleDates?: number;
  availableDays?: number;
  checkedFlexibleDates?: boolean;
  enabledFlexibleDates?: boolean;
}

interface NumberDateRange {
  start: number;
  end: number;
}

interface DateRange {
  start: Date;
  end: Date;
}

/**
 * determine dateRange based on prepopulation's values (departure, dateRange)
 * @param {*} param
 * @param {Number} maxItemsAmount
 */
export const getDateRange = (
  { departure, dateRange }: { departure: NumberDateRange; dateRange: NumberDateRange },
  maxItemsAmount?: number
): Partial<DateRange> => {
  if (!maxItemsAmount) {
    return computeDateRange(dateRange);
  }
  let startRange = 0;
  const range = dateRange || departure;
  if (range && range.start) {
    startRange = dateRange.start;
  }
  const start = dayjs().add(startRange, 'day');
  return {
    start: start.toDate(),
    end: start
      .add(maxItemsAmount - 1, 'month')
      .endOf('month')
      .toDate()
  };
};

/**
 * validates that the prepopulated dateRange is valid for the configured departure. ("departure": {"start": 0,"end": 365},  "dateRange": {"start": "2023-01-15", "end": "2024-01-10",})
 * @param {*} param
 */
export const validateDateRange = ({
  departure,
  dateRange
}: {
  departure: NumberDateRange;
  dateRange: any;
}): Partial<DateRange> => {
  let valid = false;
  const start = dayjs().add(departure ? departure.start : 0, 'day');
  const end = dayjs().add(departure ? departure.start + departure.end - 1 : 179, 'day');
  dayjs.extend(isBetween);
  if (dateRange.start && dateRange.end) {
    valid =
      dayjs(dateRange.start).isBetween(start, end, 'day', '[]') &&
      dayjs(dateRange.end).isBetween(start, end, 'day', '[]');
  } else if (dateRange.start) {
    valid = dayjs(dateRange.start).isBetween(start, end, 'day', '[]');
  }
  if (valid) {
    return dateRange;
  }
  return {};
};

type DateRangeSelectorRef = RefObject<Pick<DatesRangeSelector, 'clear'>>;

interface UseDateRange {
  value: StringDateRange;
  defaultValue: StringDateRange;
  onChange: (value: StringDateRange) => void;
  onClear: () => void;
  clear: (requestFocus?: boolean) => void;
  ref: DateRangeSelectorRef;
}

const useDateRange = ({ dispatch, value, initValue }: $TSFixMe): UseDateRange => {
  const ref: DateRangeSelectorRef = useRef(null);

  const onChange: UseDateRange['onChange'] = useCallback(
    (dateRange: any) => {
      const newDateRange = {
        start:
          dateRange.start?.checkedFlexibleDates !== undefined
            ? dateRange.start?.start
            : dateRange.start,
        end:
          dateRange.start?.checkedFlexibleDates !== undefined
            ? dateRange.start?.end
            : dateRange.end,
        flexibleDates:
          dateRange.start?.flexibleDates !== undefined
            ? dateRange.start?.flexibleDates
            : dateRange.flexibleDates,
        availableDays:
          dateRange.start?.availableDays !== undefined
            ? dateRange.start?.availableDays
            : dateRange.availableDays,
        type: dateRange.type,
        checkedFlexibleDates:
          dateRange.start?.checkedFlexibleDates !== undefined
            ? dateRange.start?.checkedFlexibleDates
            : dateRange.checkedFlexibleDates
      };
      const newValue =
        value.start !== undefined ? { start: value.start?.start, end: value.start?.end } : {};
      if (!isSameDateRange(newDateRange, newValue)) {
        dispatch({ type: 'DATE_RANGE_CHANGE', payload: newDateRange });
      }
    },
    [dispatch, value]
  );

  const onClear = useCallback((): void => {
    dispatch({ type: 'DATE_RANGE_CLEAR' });
  }, [dispatch]);

  const clear: UseDateRange['clear'] = useCallback((requestFocus = false): void => {
    if (ref.current) {
      ref.current.clear(requestFocus);
    }
  }, []);

  return useMemo(() => ({ value, defaultValue: initValue, onChange, onClear, ref, clear }), [
    clear,
    onChange,
    onClear,
    value,
    initValue
  ]);
};

export default useDateRange;
